import { useState } from "react";
import { Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/admin/ImageUploader";
import { toast } from "sonner";

/**
 * Generic section editor: renders schema-driven form for each section.
 * data: full content object (with sections + branding)
 * activeKey: section key currently being edited
 */
export default function SectionEditor({ activeKey, content, setContent, onSave, saving }) {
  if (activeKey === "branding") return <BrandingEditor content={content} setContent={setContent} onSave={onSave} saving={saving} />;

  const section = content.sections?.[activeKey];
  if (!section) return <div className="text-slate-500">Unknown section</div>;

  const update = (next) => {
    setContent({ ...content, sections: { ...content.sections, [activeKey]: next } });
  };
  const updateField = (k, v) => update({ ...section, [k]: v });

  const visible = section.visible !== false;

  return (
    <div data-testid={`section-editor-${activeKey}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">{section.label || activeKey}</h2>
          <p className="text-sm text-slate-500">Edit content for this section.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200" data-testid={`visibility-toggle-${activeKey}`}>
            {visible ? <Eye className="w-4 h-4 text-teal-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
            <span className="text-xs font-semibold text-slate-600">Visible on website</span>
            <Switch
              checked={visible}
              onCheckedChange={(c) => updateField("visible", c)}
              data-testid={`visibility-switch-${activeKey}`}
            />
          </div>
          <button onClick={onSave} disabled={saving} data-testid="section-save-btn" className="btn-primary !py-2.5 disabled:opacity-60">
            {saving ? "Saving..." : <><Save className="w-4 h-4" /> Save Changes</>}
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-6">
        {/* Common fields */}
        <Card>
          <Row>
            {section.kicker !== undefined && (
              <Field label="Kicker (small caps text)" value={section.kicker} onChange={(v) => updateField("kicker", v)} testid={`field-${activeKey}-kicker`} />
            )}
            {section.title !== undefined && (
              <Field label="Heading" value={section.title} onChange={(v) => updateField("title", v)} testid={`field-${activeKey}-title`} />
            )}
          </Row>
          {section.subtitle !== undefined && (
            <Field label="Subtitle" value={section.subtitle} onChange={(v) => updateField("subtitle", v)} testid={`field-${activeKey}-subtitle`} />
          )}
          {section.description !== undefined && (
            <Field label="Description" value={section.description} onChange={(v) => updateField("description", v)} type="textarea" testid={`field-${activeKey}-desc`} />
          )}
          {section.ctaLabel !== undefined && (
            <Field label="Button Label" value={section.ctaLabel} onChange={(v) => updateField("ctaLabel", v)} testid={`field-${activeKey}-cta`} />
          )}
          {section.tagline !== undefined && (
            <Field label="Tagline" value={section.tagline} onChange={(v) => updateField("tagline", v)} type="textarea" testid={`field-${activeKey}-tagline`} />
          )}
          {section.copyright !== undefined && (
            <Field label="Copyright" value={section.copyright} onChange={(v) => updateField("copyright", v)} testid={`field-${activeKey}-copy`} />
          )}
        </Card>

        {/* Single image */}
        {section.image !== undefined && (
          <Card title="Image">
            <ImageUploader value={section.image} onChange={(v) => updateField("image", v)} testid={`image-${activeKey}`} />
          </Card>
        )}

        {/* Section specific */}
        {activeKey === "hero" && (
          <>
            <HeroSideCardEditor
              sideCard={section.sideCard || {}}
              onChange={(sideCard) => updateField("sideCard", sideCard)}
            />
            <SlidesEditor slides={section.slides || []} onChange={(slides) => updateField("slides", slides)} />
          </>
        )}

        {activeKey === "about" && (
          <StatsEditor stats={section.stats || []} onChange={(stats) => updateField("stats", stats)} />
        )}

        {(activeKey === "courses" || activeKey === "colleges") && (
          <CardItemsEditor
            items={section.items || []}
            onChange={(items) => updateField("items", items)}
            fields={activeKey === "courses"
              ? [{ k: "title", l: "Title" }, { k: "duration", l: "Duration" }, { k: "description", l: "Description", type: "textarea" }]
              : [{ k: "name", l: "College Name" }, { k: "code", l: "Code / Affiliation" }]}
            withImage
            kind={activeKey}
          />
        )}

        {activeKey === "whyChooseUs" && (
          <IconItemsEditor items={section.items || []} onChange={(items) => updateField("items", items)} />
        )}

        {activeKey === "faculty" && (
          <CardItemsEditor
            items={section.items || []}
            onChange={(items) => updateField("items", items)}
            fields={[{ k: "name", l: "Name" }, { k: "role", l: "Role" }]}
            withImage
            kind="faculty"
          />
        )}

        {activeKey === "gallery" && (
          <GalleryEditor images={section.images || []} onChange={(images) => updateField("images", images)} />
        )}

        {activeKey === "testimonials" && (
          <TestimonialsEditor items={section.items || []} onChange={(items) => updateField("items", items)} />
        )}

        {activeKey === "contact" && (
          <>
            <Card title="Contact details">
              <Row>
                <Field label="Primary Phone (legacy)" value={section.phone} onChange={(v) => updateField("phone", v)} testid="field-contact-phone" />
                <Field label="Email" value={section.email} onChange={(v) => updateField("email", v)} testid="field-contact-email" />
              </Row>
              <Field label="Address" value={section.address} onChange={(v) => updateField("address", v)} type="textarea" testid="field-contact-address" />
              <Field label="Google Maps Link (opens on address click)" value={section.mapLink} onChange={(v) => updateField("mapLink", v)} testid="field-contact-map-link" />
              <Field label="Google Maps Embed URL (unused after redesign)" value={section.mapEmbed} onChange={(v) => updateField("mapEmbed", v)} testid="field-contact-map" />
            </Card>

            <PhoneListEditor
              phones={section.phones || []}
              onChange={(phones) => updateField("phones", phones)}
            />
          </>
        )}

        {activeKey === "consultancy" && (
          <>
            <Card title="Programs (BAMS · BHMS · MBBS · BDS)" action={<AddBtn onClick={() => updateField("programs", [...(section.programs || []), { code: "", title: "" }])} />}>
              <div className="grid md:grid-cols-2 gap-3">
                {(section.programs || []).map((p, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 p-4 bg-slate-50/40" data-testid={`consultancy-program-item-${i}`}>
                    <div className="flex justify-between items-center mb-2">
                      <div className="text-xs font-semibold text-slate-500">Program {i + 1}</div>
                      <RemoveBtn onClick={() => updateField("programs", (section.programs || []).filter((_, j) => j !== i))} />
                    </div>
                    <Row>
                      <Field label="Code" value={p.code} onChange={(v) => updateField("programs", (section.programs || []).map((x, j) => j === i ? { ...x, code: v } : x))} />
                      <Field label="Full Title" value={p.title} onChange={(v) => updateField("programs", (section.programs || []).map((x, j) => j === i ? { ...x, title: v } : x))} />
                    </Row>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Highlights" action={<AddBtn onClick={() => updateField("highlights", [...(section.highlights || []), "New highlight"])} />}>
              <div className="grid md:grid-cols-2 gap-3">
                {(section.highlights || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={h}
                      onChange={(e) => updateField("highlights", (section.highlights || []).map((x, j) => j === i ? e.target.value : x))}
                      className="flex-1 text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-[#1E3A8A] focus:outline-none"
                      data-testid={`consultancy-highlight-${i}`}
                    />
                    <RemoveBtn onClick={() => updateField("highlights", (section.highlights || []).filter((_, j) => j !== i))} />
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Contact Form">
              <Row>
                <Field label="Form Title" value={section.formTitle} onChange={(v) => updateField("formTitle", v)} testid="field-consultancy-form-title" />
                <Field label="Form Subtitle" value={section.formSubtitle} onChange={(v) => updateField("formSubtitle", v)} testid="field-consultancy-form-subtitle" />
              </Row>
              <Field label="Submit Button Label" value={section.ctaLabel} onChange={(v) => updateField("ctaLabel", v)} testid="field-consultancy-cta" />
            </Card>
          </>
        )}

        {activeKey === "footer" && (
          <Card title="Social Links">
            <Row>
              <Field label="Facebook URL" value={section.socials?.facebook} onChange={(v) => updateField("socials", { ...(section.socials || {}), facebook: v })} testid="field-footer-fb" />
              <Field label="Instagram URL" value={section.socials?.instagram} onChange={(v) => updateField("socials", { ...(section.socials || {}), instagram: v })} testid="field-footer-ig" />
            </Row>
            <Row>
              <Field label="YouTube URL" value={section.socials?.youtube} onChange={(v) => updateField("socials", { ...(section.socials || {}), youtube: v })} testid="field-footer-yt" />
              <Field label="Twitter URL" value={section.socials?.twitter} onChange={(v) => updateField("socials", { ...(section.socials || {}), twitter: v })} testid="field-footer-tw" />
            </Row>
          </Card>
        )}
      </div>
    </div>
  );
}

function BrandingEditor({ content, setContent, onSave, saving }) {
  const b = content.branding || {};
  const set = (k, v) => setContent({ ...content, branding: { ...b, [k]: v } });
  return (
    <div data-testid="section-editor-branding">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Branding</h2>
          <p className="text-sm text-slate-500">Site identity & primary contact.</p>
        </div>
        <button onClick={onSave} disabled={saving} className="btn-primary !py-2.5 disabled:opacity-60" data-testid="branding-save-btn">
          {saving ? "Saving..." : <><Save className="w-4 h-4" /> Save Changes</>}
        </button>
      </div>
      <div className="mt-8 space-y-6">
        <Card title="Logo">
          <div className="grid md:grid-cols-[260px_1fr] gap-6 items-start">
            <ImageUploader
              value={b.logoUrl}
              onChange={(v) => set("logoUrl", v)}
              testid="branding-logo-uploader"
              aspect="aspect-square"
            />
            <div className="text-sm text-slate-600 space-y-2">
              <p className="font-semibold text-[#0F172A]">Recommended</p>
              <ul className="list-disc pl-5 space-y-1 text-xs text-slate-500">
                <li>Square or near-square PNG with transparent background.</li>
                <li>Minimum 256×256 px. Larger is fine — it scales down.</li>
                <li>Leave blank to fall back to the "S" letter badge.</li>
              </ul>
              {b.logoUrl && (
                <button
                  type="button"
                  onClick={() => set("logoUrl", "")}
                  className="text-xs font-semibold text-red-600 hover:underline mt-2"
                  data-testid="branding-logo-clear"
                >
                  Remove logo
                </button>
              )}
            </div>
          </div>
        </Card>

        <Card title="Identity">
          <Row>
            <Field label="Site Name" value={b.siteName} onChange={(v) => set("siteName", v)} testid="field-branding-name" />
            <Field label="Tagline" value={b.tagline} onChange={(v) => set("tagline", v)} testid="field-branding-tagline" />
          </Row>
          <Row>
            <Field label="Logo Word" value={b.logoText} onChange={(v) => set("logoText", v)} testid="field-branding-logo" />
            <Field label="Logo Accent" value={b.logoAccent} onChange={(v) => set("logoAccent", v)} testid="field-branding-logo-accent" />
          </Row>
          <Row>
            <Field label="Primary Phone" value={b.primaryPhone} onChange={(v) => set("primaryPhone", v)} testid="field-branding-phone" />
            <Field label="Primary Email" value={b.primaryEmail} onChange={(v) => set("primaryEmail", v)} testid="field-branding-email" />
          </Row>
        </Card>
      </div>
    </div>
  );
}

/* Sub-editors */
function HeroSideCardEditor({ sideCard, onChange }) {
  const update = (patch) => onChange({ ...sideCard, ...patch });
  const stats = sideCard.stats || [];
  const setStats = (next) => update({ stats: next });
  return (
    <Card
      title="Hero Side Card (numbers on right)"
      action={
        <AddBtn onClick={() => setStats([...stats, { value: "0+", label: "Label" }])} />
      }
    >
      <p className="text-xs text-slate-500 mb-4">The trust card on the right side of the hero banner. Edit the tagline, small badge and the number tiles below.</p>
      <Row>
        <Field label="Small Badge" value={sideCard.badge} onChange={(v) => update({ badge: v })} testid="field-hero-sidecard-badge" />
        <Field label="Card Title" value={sideCard.title} onChange={(v) => update({ title: v })} type="textarea" testid="field-hero-sidecard-title" />
      </Row>
      <div className="mt-2 grid sm:grid-cols-2 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-4 bg-slate-50/40" data-testid={`hero-stat-row-${i}`}>
            <div className="flex justify-between items-center mb-2">
              <div className="text-xs font-semibold text-slate-500">Number {i + 1}</div>
              <RemoveBtn onClick={() => setStats(stats.filter((_, j) => j !== i))} testid={`hero-stat-remove-${i}`} />
            </div>
            <Row>
              <Field label="Value (e.g. 2500+)" value={s.value} onChange={(v) => setStats(stats.map((x, j) => j === i ? { ...x, value: v } : x))} testid={`hero-stat-value-${i}`} />
              <Field label="Label (e.g. Students)" value={s.label} onChange={(v) => setStats(stats.map((x, j) => j === i ? { ...x, label: v } : x))} testid={`hero-stat-label-${i}`} />
            </Row>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PhoneListEditor({ phones, onChange }) {
  const add = () => onChange([...(phones || []), { label: "New", number: "" }]);
  return (
    <Card title="Phone Numbers (3 placeholders)" action={<AddBtn onClick={add} />}>
      <p className="text-xs text-slate-500 mb-3">Add up to 3 phone numbers with labels (e.g. Admissions, Counsellor, Support). Leave a number blank to hide it from the site.</p>
      <div className="space-y-3">
        {(phones || []).map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-4 bg-slate-50/40" data-testid={`phone-row-${i}`}>
            <div className="flex justify-between items-center mb-2">
              <div className="text-xs font-semibold text-slate-500">Phone {i + 1}</div>
              <RemoveBtn onClick={() => onChange(phones.filter((_, j) => j !== i))} testid={`phone-remove-${i}`} />
            </div>
            <Row>
              <Field label="Label" value={p.label} onChange={(v) => onChange(phones.map((x, j) => j === i ? { ...x, label: v } : x))} testid={`phone-label-${i}`} />
              <Field label="Number" value={p.number} onChange={(v) => onChange(phones.map((x, j) => j === i ? { ...x, number: v } : x))} testid={`phone-number-${i}`} />
            </Row>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SlidesEditor({ slides, onChange }) {
  return (
    <Card title="Hero Slides" action={<AddBtn onClick={() => onChange([...slides, { title: "New slide", subtitle: "", image: "", primaryCta: "Enquire Now", secondaryCta: "Call Now" }])} />}>
      <div className="space-y-4">
        {slides.map((s, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 bg-slate-50/40" data-testid={`hero-slide-${i}`}>
            <div className="flex justify-between items-start gap-3 mb-3">
              <div className="text-xs font-semibold text-slate-500">Slide {i + 1}</div>
              <RemoveBtn onClick={() => onChange(slides.filter((_, j) => j !== i))} testid={`hero-slide-remove-${i}`} />
            </div>
            <Row>
              <Field label="Title" value={s.title} onChange={(v) => onChange(slides.map((x, j) => j === i ? { ...x, title: v } : x))} />
              <Field label="Subtitle" value={s.subtitle} onChange={(v) => onChange(slides.map((x, j) => j === i ? { ...x, subtitle: v } : x))} type="textarea" />
            </Row>
            <Row>
              <Field label="Primary CTA" value={s.primaryCta} onChange={(v) => onChange(slides.map((x, j) => j === i ? { ...x, primaryCta: v } : x))} />
              <Field label="Secondary CTA" value={s.secondaryCta} onChange={(v) => onChange(slides.map((x, j) => j === i ? { ...x, secondaryCta: v } : x))} />
            </Row>
            <div className="mt-4">
              <Label>Banner Image</Label>
              <ImageUploader value={s.image} onChange={(v) => onChange(slides.map((x, j) => j === i ? { ...x, image: v } : x))} testid={`hero-slide-image-${i}`} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function StatsEditor({ stats, onChange }) {
  return (
    <Card title="Stats" action={<AddBtn onClick={() => onChange([...stats, { value: "100", label: "Label" }])} />}>
      <div className="grid sm:grid-cols-2 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-4 bg-slate-50/40">
            <div className="flex justify-between items-center mb-2">
              <div className="text-xs font-semibold text-slate-500">Stat {i + 1}</div>
              <RemoveBtn onClick={() => onChange(stats.filter((_, j) => j !== i))} />
            </div>
            <Row>
              <Field label="Value" value={s.value} onChange={(v) => onChange(stats.map((x, j) => j === i ? { ...x, value: v } : x))} />
              <Field label="Label" value={s.label} onChange={(v) => onChange(stats.map((x, j) => j === i ? { ...x, label: v } : x))} />
            </Row>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CardItemsEditor({ items, onChange, fields, withImage, kind }) {
  const empty = fields.reduce((acc, f) => ({ ...acc, [f.k]: "" }), withImage ? { image: "" } : {});
  return (
    <Card title="Items" action={<AddBtn onClick={() => onChange([...items, empty])} />}>
      <div className="space-y-4">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 bg-slate-50/40" data-testid={`${kind}-item-${i}`}>
            <div className="flex justify-between items-start gap-3 mb-3">
              <div className="text-xs font-semibold text-slate-500">Item {i + 1}</div>
              <RemoveBtn onClick={() => onChange(items.filter((_, j) => j !== i))} testid={`${kind}-item-remove-${i}`} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                {fields.map((f) => (
                  <Field
                    key={f.k}
                    label={f.l}
                    value={it[f.k]}
                    type={f.type}
                    onChange={(v) => onChange(items.map((x, j) => j === i ? { ...x, [f.k]: v } : x))}
                  />
                ))}
              </div>
              {withImage && (
                <div>
                  <Label>Image</Label>
                  <ImageUploader value={it.image} onChange={(v) => onChange(items.map((x, j) => j === i ? { ...x, image: v } : x))} testid={`${kind}-image-${i}`} aspect="aspect-[4/3]" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

const ICON_NAMES = ["GraduationCap", "Microscope", "Briefcase", "ShieldCheck", "BookOpen", "Users", "Stethoscope", "Award"];

function IconItemsEditor({ items, onChange }) {
  return (
    <Card title="Highlights" action={<AddBtn onClick={() => onChange([...items, { icon: "GraduationCap", title: "New highlight", description: "" }])} />}>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((it, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 bg-slate-50/40">
            <div className="flex justify-between items-start gap-3 mb-3">
              <div className="text-xs font-semibold text-slate-500">Highlight {i + 1}</div>
              <RemoveBtn onClick={() => onChange(items.filter((_, j) => j !== i))} />
            </div>
            <Label>Icon</Label>
            <select
              value={it.icon}
              onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, icon: e.target.value } : x))}
              className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 mt-1.5 mb-3 bg-white"
            >
              {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Field label="Title" value={it.title} onChange={(v) => onChange(items.map((x, j) => j === i ? { ...x, title: v } : x))} />
            <Field label="Description" value={it.description} onChange={(v) => onChange(items.map((x, j) => j === i ? { ...x, description: v } : x))} type="textarea" />
          </div>
        ))}
      </div>
    </Card>
  );
}

function GalleryEditor({ images, onChange }) {
  return (
    <Card title="Gallery Images" action={<AddBtn onClick={() => onChange([...images, ""])} />}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {images.map((img, i) => (
          <div key={i} className="space-y-2" data-testid={`gallery-edit-${i}`}>
            <ImageUploader value={img} onChange={(v) => onChange(images.map((x, j) => j === i ? v : x))} testid={`gallery-img-${i}`} aspect="aspect-square" />
            <button onClick={() => onChange(images.filter((_, j) => j !== i))} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function TestimonialsEditor({ items, onChange }) {
  return (
    <Card title="Testimonials" action={<AddBtn onClick={() => onChange([...items, { name: "Student name", role: "Our Student", quote: "" }])} />}>
      <div className="space-y-3">
        {items.map((t, i) => (
          <div key={i} className="rounded-xl border border-slate-200 p-5 bg-slate-50/40">
            <div className="flex justify-between items-start gap-3 mb-3">
              <div className="text-xs font-semibold text-slate-500">Testimonial {i + 1}</div>
              <RemoveBtn onClick={() => onChange(items.filter((_, j) => j !== i))} />
            </div>
            <Row>
              <Field label="Name" value={t.name} onChange={(v) => onChange(items.map((x, j) => j === i ? { ...x, name: v } : x))} />
              <Field label="Role / Tag" value={t.role} onChange={(v) => onChange(items.map((x, j) => j === i ? { ...x, role: v } : x))} />
            </Row>
            <Field label="Quote" value={t.quote} onChange={(v) => onChange(items.map((x, j) => j === i ? { ...x, quote: v } : x))} type="textarea" />
          </div>
        ))}
      </div>
    </Card>
  );
}

/* Primitive UI */
function Card({ title, action, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6">
      {(title || action) && (
        <div className="flex items-center justify-between mb-5">
          {title && <h3 className="font-bold text-[#0F172A]">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div className="grid md:grid-cols-2 gap-4 mb-3">{children}</div>;
}

function Label({ children }) {
  return <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{children}</label>;
}

function Field({ label, value, onChange, type = "text", testid }) {
  if (type === "textarea") {
    return (
      <div>
        <Label>{label}</Label>
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          data-testid={testid}
          className="mt-1.5 w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-[#1E3A8A] focus:outline-none resize-y"
        />
      </div>
    );
  }
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-[#1E3A8A] focus:outline-none"
      />
    </div>
  );
}

function AddBtn({ onClick }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A8A] hover:bg-[#1E3A8A]/5 px-3 py-1.5 rounded-lg transition">
      <Plus className="w-3.5 h-3.5" /> Add
    </button>
  );
}
function RemoveBtn({ onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-slate-400 hover:text-red-600 transition" aria-label="remove">
      <Trash2 className="w-4 h-4" />
    </button>
  );
}
